import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowLeft,
    Check,
    ChevronRight,
    Copy,
    Download,
    Facebook,
    Globe,
    Image as ImageIcon,
    Instagram,
    Linkedin,
    Mic,
    Music2,
    SendHorizontal,
    Sparkles,
    Twitter,
    Video,
    Youtube,
    Loader2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "@/lib/api";

/* ----------------------------- small helpers ----------------------------- */

const PLATFORM_META = {
    tiktok: { label: "TikTok", icon: Music2, tint: "#FF5470" },
    instagram: { label: "Instagram", icon: Instagram, tint: "#00F5D4" },
    facebook: { label: "Facebook", icon: Facebook, tint: "#2F58CD" },
    youtube: { label: "YouTube", icon: Youtube, tint: "#FF5470" },
    linkedin: { label: "LinkedIn", icon: Linkedin, tint: "#2F58CD" },
    twitter: { label: "X", icon: Twitter, tint: "#8A93A6" },
    x: { label: "X", icon: Twitter, tint: "#8A93A6" },
    reddit: { label: "Reddit", icon: Globe, tint: "#FF5470" },
};

function platformInfo(id) {
    const key = (id || "").toLowerCase();
    return (
        PLATFORM_META[key] || {
            label: key || "Everywhere",
            icon: Globe,
            tint: "#8A93A6",
        }
    );
}

function formatIcon(formatText) {
    const t = (formatText || "").toLowerCase();
    if (t.includes("video") || t.includes("reel") || t.includes("short"))
        return Video;
    if (t.includes("carousel") || t.includes("image") || t.includes("static"))
        return ImageIcon;
    if (t.includes("live") || t.includes("audio") || t.includes("podcast"))
        return Mic;
    return Sparkles;
}

/* -------------------------------- Roadmap -------------------------------- */

export default function StrategyRoadmap({
    roadmap,
    onStartNew,
    onSentToPlanner,
}) {
    const [sending, setSending] = useState(false);
    const [expanded, setExpanded] = useState(() =>
        Object.fromEntries((roadmap?.weeks || []).map((w) => [w.week_number, true]))
    );

    const toggleWeek = (n) =>
        setExpanded((prev) => ({ ...prev, [n]: !prev[n] }));

    const handleExport = () => {
        try {
            const blob = new Blob([JSON.stringify(roadmap, null, 2)], {
                type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const slug = (roadmap.niche || "strategy")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");
            a.href = url;
            a.download = `smartpublish-strategy-${slug}-${roadmap.duration_days}d.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Strategy exported", {
                description: `Downloaded ${slug}-${roadmap.duration_days}d.json`,
            });
        } catch (err) {
            toast.error("Export failed", {
                description: err?.message || String(err),
            });
        }
    };

    const handleSendToPlanner = async () => {
        if (sending) return;
        setSending(true);
        let count = 0;
        try {
            const today = new Date();
            today.setHours(9, 0, 0, 0);
            // Flatten all days across weeks preserving day_number sequencing.
            const days = (roadmap.weeks || []).flatMap((w) => w.days || []);
            for (const d of days) {
                const when = new Date(today);
                // Offset by (day_number - 1) so Day 1 maps to today @ 9:00.
                when.setDate(when.getDate() + (d.day_number - 1));
                const payload = {
                    title: d.title,
                    caption: d.caption,
                    hashtags: d.hashtags || [],
                    platforms:
                        (d.platform_routing || []).length > 0
                            ? d.platform_routing
                            : ["instagram"],
                    scheduled_for: when.toISOString(),
                };
                try {
                    await api.post("/posts/schedule", payload);
                    count += 1;
                } catch (err) {
                    // Keep going even if one fails — report final count.
                    // eslint-disable-next-line no-console
                    console.warn("[strategy] schedule failed for day", d.day_number, err);
                }
            }
            toast.success(`Added ${count} posts to your Content Library.`, {
                description: "Open Dashboard to copy, tweak and publish.",
                action: {
                    label: "View library",
                    onClick: () =>
                        window.location.assign(
                            `${window.location.origin}/dashboard`
                        ),
                },
            });
            onSentToPlanner?.(count);
        } catch (err) {
            toast.error("Send to planner failed", {
                description: err?.message || String(err),
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col gap-6"
            data-testid="strategy-roadmap"
        >
            {/* Header */}
            <div className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-[#1F2A40] bg-[#111726] p-5 sm:flex-row sm:items-center">
                <div className="flex flex-col gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F5D4]">
                        your growth strategy
                    </span>
                    <h2
                        className="text-2xl font-medium text-[#E8ECF5]"
                        data-testid="roadmap-niche"
                    >
                        {roadmap.niche} ·{" "}
                        <span className="sp-glow-mint">
                            {roadmap.duration_days} days
                        </span>
                    </h2>
                    {roadmap.request?.tone?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {roadmap.request.tone.map((t) => (
                                <span
                                    key={t}
                                    className="rounded-full border border-[#2F58CD]/50 bg-[#2F58CD]/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em] text-[#7DA1FF]"
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-start gap-2 sm:items-end">
                    {typeof roadmap.balance_after === "number" && (
                        <span
                            className="font-mono text-[11px] text-[#8A93A6]"
                            data-testid="roadmap-balance"
                        >
                            Balance:{" "}
                            <span className="text-[#E8ECF5]">
                                {roadmap.balance_after} NXT
                            </span>
                        </span>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handleExport}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-[#1F2A40] bg-[#111726] px-3 py-1.5 text-xs font-medium text-[#E8ECF5] transition-all hover:border-[#00F5D4]/50 hover:text-[#00F5D4]"
                            data-testid="roadmap-export-btn"
                        >
                            <Download className="h-3.5 w-3.5" />
                            Export Strategy
                        </button>
                        <button
                            type="button"
                            onClick={handleSendToPlanner}
                            disabled={sending}
                            className={[
                                "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-[#0B0F19] transition-all",
                                sending
                                    ? "cursor-wait bg-[#00F5D4]/60"
                                    : "bg-[#00F5D4] hover:shadow-[0_0_20px_rgba(0,245,212,0.5)]",
                            ].join(" ")}
                            data-testid="roadmap-send-to-planner-btn"
                        >
                            {sending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <SendHorizontal className="h-3.5 w-3.5" />
                            )}
                            Send to Content Planner
                        </button>
                    </div>
                </div>
            </div>

            {/* Weeks */}
            <div className="flex flex-col gap-4">
                {(roadmap.weeks || []).map((w) => (
                    <WeekCard
                        key={w.week_number}
                        week={w}
                        expanded={!!expanded[w.week_number]}
                        onToggle={() => toggleWeek(w.week_number)}
                    />
                ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-center pt-4">
                <button
                    type="button"
                    onClick={onStartNew}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#1F2A40] bg-[#111726] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A93A6] transition-all hover:border-[#00F5D4]/50 hover:text-[#00F5D4]"
                    data-testid="roadmap-start-new-btn"
                >
                    <ArrowLeft className="h-3 w-3" />
                    Start a new strategy
                </button>
            </div>
        </motion.section>
    );
}

/* --------------------------------- Week ---------------------------------- */

function WeekCard({ week, expanded, onToggle }) {
    return (
        <div
            className="overflow-hidden rounded-2xl border border-[#1F2A40] bg-[#111726]"
            data-testid={`roadmap-week-${week.week_number}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-start justify-between gap-4 border-b border-[#1F2A40]/70 px-5 py-4 text-left transition-colors hover:bg-[#1A2235]/40"
                data-testid={`roadmap-week-toggle-${week.week_number}`}
            >
                <div className="flex flex-col gap-1">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F5D4]">
                        week {week.week_number}
                    </span>
                    <p className="max-w-prose text-sm text-[#E8ECF5]">
                        {week.milestone || "—"}
                    </p>
                </div>
                <motion.span
                    animate={{ rotate: expanded ? 90 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-1 shrink-0 text-[#8A93A6]"
                >
                    <ChevronRight className="h-5 w-5" />
                </motion.span>
            </button>
            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        key="days"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        <div className="flex flex-col divide-y divide-[#1F2A40]/60">
                            {(week.days || []).map((d) => (
                                <DayRow key={d.day_number} day={d} />
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

/* ---------------------------------- Day ---------------------------------- */

function DayRow({ day }) {
    const [copied, setCopied] = useState(false);
    const Fmt = formatIcon(day.format_recommendation);
    const copy = async () => {
        const payload = [
            day.content_hook,
            day.title,
            day.caption,
            (day.hashtags || []).join(" "),
        ]
            .filter((s) => s && s.trim())
            .join("\n\n");
        try {
            await navigator.clipboard.writeText(payload);
            setCopied(true);
            toast.success(`Day ${day.day_number} copied to clipboard`);
            setTimeout(() => setCopied(false), 1600);
        } catch (err) {
            toast.error("Copy failed", {
                description: err?.message || String(err),
            });
        }
    };

    return (
        <div
            className="flex flex-col gap-3 p-5"
            data-testid={`roadmap-day-${day.day_number}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span
                        className="inline-flex h-9 min-w-[36px] items-center justify-center rounded-lg border border-[#1F2A40] bg-[#0B0F19] px-2 font-mono text-xs font-semibold tabular-nums text-[#E8ECF5]"
                        data-testid={`roadmap-day-number-${day.day_number}`}
                    >
                        D{day.day_number}
                    </span>
                    <div className="flex flex-col gap-0.5">
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                            {day.day_of_week}
                        </span>
                        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#00F5D4]">
                            <Fmt className="h-3 w-3" />
                            {day.format_recommendation || "Post"}
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {(day.platform_routing || []).map((p) => {
                        const meta = platformInfo(p);
                        const Icon = meta.icon;
                        return (
                            <span
                                key={p}
                                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em]"
                                style={{
                                    borderColor: `${meta.tint}66`,
                                    backgroundColor: `${meta.tint}15`,
                                    color: meta.tint,
                                }}
                                data-testid={`roadmap-day-${day.day_number}-platform-${p}`}
                            >
                                <Icon className="h-2.5 w-2.5" />
                                {meta.label}
                            </span>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <h4 className="text-lg font-medium leading-snug text-[#E8ECF5]">
                    {day.content_hook}
                </h4>
                {day.title && (
                    <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-[#00F5D4]">
                        {day.title}
                    </p>
                )}
                {day.caption && (
                    <p className="line-clamp-4 text-sm leading-relaxed text-[#8A93A6]">
                        {day.caption}
                    </p>
                )}
                {(day.hashtags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                        {day.hashtags.slice(0, 8).map((h, i) => (
                            <span
                                key={`${h}-${i}`}
                                className="rounded-md border border-[#1F2A40] bg-[#0B0F19] px-1.5 py-0.5 font-mono text-[10px] text-[#8A93A6]"
                            >
                                {h.startsWith("#") ? h : `#${h}`}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div>
                <button
                    type="button"
                    onClick={copy}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#1F2A40] bg-[#111726] px-2.5 py-1 text-xs font-medium text-[#E8ECF5] transition-all hover:border-[#00F5D4]/50 hover:text-[#00F5D4]"
                    data-testid={`roadmap-day-copy-${day.day_number}`}
                >
                    {copied ? (
                        <Check className="h-3 w-3 text-[#00F5D4]" />
                    ) : (
                        <Copy className="h-3 w-3" />
                    )}
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
        </div>
    );
}
